import { SeatInventoryItem, SeatRequestItem } from '../../services/scheduledSeatService';

export const fmtDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

export const ageMinutes = (value?: string | null) => {
  if (!value) return 0;
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
};

export const requestSlaState = (request: SeatRequestItem): { tone: 'green' | 'amber' | 'rose'; label: string; rank: number } => {
  const laneStatus = String(request.status || '').toUpperCase();
  if (['CLOSED_WON', 'CLOSED_LOST', 'DECLINED'].includes(laneStatus)) {
    return { tone: 'green', label: 'CLOSED', rank: 0 };
  }
  const mins = ageMinutes(request.updatedAt || request.createdAt);
  if (mins >= 90) return { tone: 'rose', label: `RED · ${mins}m`, rank: 3 };
  if (mins >= 30) return { tone: 'amber', label: `AMBER · ${mins}m`, rank: 2 };
  return { tone: 'green', label: `GREEN · ${mins}m`, rank: 1 };
};

export const badgeClass = (tone: 'blue' | 'green' | 'amber' | 'slate' | 'rose') => {
  if (tone === 'green') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (tone === 'amber') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (tone === 'rose') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (tone === 'blue') return 'border-sky-200 bg-sky-50 text-sky-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

export const statusTone = (status?: string): 'blue' | 'green' | 'amber' | 'slate' | 'rose' => {
  const s = String(status || '').toUpperCase();
  if (s === 'PUBLISHED' || s === 'OFFER_SENT' || s === 'AWAITING_CLIENT' || s === 'CLOSED_WON') return 'green';
  if (s === 'APPROVAL_PENDING' || s === 'PENDING_APPROVAL' || s === 'ALTERNATIVE_SENT') return 'amber';
  if (s === 'DECLINED' || s === 'CLOSED_LOST' || s === 'REJECTED' || s === 'CANCELLED' || s === 'EXPIRED') return 'rose';
  if (s === 'BOOKED' || s === 'CONFIRMED') return 'blue';
  return 'slate';
};

export const requestDisplayStatus = (request: SeatRequestItem): string => {
  return String(request.lifecycleStatus || request.matchedInventory?.lifecycleStatus || request.latestOfferStatus || request.status || 'OPEN')
    .trim()
    .toUpperCase();
};

export const opsStatusTone = (opsStatus?: string): 'blue' | 'green' | 'amber' | 'slate' | 'rose' => {
  const s = String(opsStatus || 'OPEN').toUpperCase();
  if (s === 'OPEN') return 'green';
  if (s === 'CLOSED') return 'amber';
  if (s === 'DEPARTED') return 'blue';
  if (s === 'CANCELLED') return 'rose';
  return 'slate';
};

export const resolvePricingSource = (item: SeatInventoryItem): 'ROUTE_BANK' | 'OVERRIDE' | 'POLICY_DEFAULT' | 'UNKNOWN' => {
  const source = String((item as any)?.metadata?.pricing?.source || '').toUpperCase();
  if (source === 'ROUTE_BANK' || source === 'OVERRIDE' || source === 'POLICY_DEFAULT') return source;
  return 'UNKNOWN';
};

export const pricingBadgeClass = (source: string) => {
  if (source === 'ROUTE_BANK') return 'border-indigo-200 bg-indigo-50 text-indigo-700';
  if (source === 'OVERRIDE') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (source === 'POLICY_DEFAULT') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

export const routeAccent = (fromCode?: string, toCode?: string) => {
  const key = `${String(fromCode || '').toUpperCase()}-${String(toCode || '').toUpperCase()}`;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 100000;
  const palette = [
    { cardBorder: 'border-cyan-200', routeTag: 'border-cyan-200 bg-cyan-50 text-cyan-800', progress: 'from-cyan-500 to-indigo-500' },
    { cardBorder: 'border-indigo-200', routeTag: 'border-indigo-200 bg-indigo-50 text-indigo-800', progress: 'from-indigo-500 to-blue-500' },
    { cardBorder: 'border-emerald-200', routeTag: 'border-emerald-200 bg-emerald-50 text-emerald-800', progress: 'from-emerald-500 to-cyan-500' },
    { cardBorder: 'border-violet-200', routeTag: 'border-violet-200 bg-violet-50 text-violet-800', progress: 'from-violet-500 to-indigo-500' },
  ];
  return palette[hash % palette.length];
};

export const toCsvCell = (value: any) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export const normalizePassengerRows = (metadata: any): Array<{ index?: number; name: string; idNumber: string }> => {
  const source = Array.isArray(metadata?.passengers)
    ? metadata.passengers
    : (Array.isArray(metadata?.passengerDetails) ? metadata.passengerDetails : []);
  return source.map((p: any, idx: number) => ({
    index: Number(p?.index || idx + 1),
    name: String(p?.name || p?.fullName || '').trim(),
    idNumber: String(p?.idNumber || p?.passportNumber || p?.passportId || '').trim(),
  }));
};

type RejectHistoryItem = {
  at?: string;
  reason: string;
  source?: string;
};

export type DecisionHistoryItem = {
  at?: string;
  event: string;
  source?: string;
  reason?: string;
  actorName?: string;
  offerId?: string;
  seats?: number;
  unitPriceUSD?: number;
  currencyCode?: string;
};

export const extractRejectHistory = (request: Partial<SeatRequestItem> & { metadata?: any; notes?: string | null }): RejectHistoryItem[] => {
  const fromMetadataRaw = Array.isArray((request as any)?.metadata?.rejectHistory)
    ? (request as any).metadata.rejectHistory
    : [];
  const fromMetadata = fromMetadataRaw
    .map((item: any) => ({
      at: String(item?.at || '').trim() || undefined,
      reason: String(item?.reason || '').trim(),
      source: String(item?.source || '').trim() || undefined,
    }))
    .filter((item: RejectHistoryItem) => item.reason);

  if (fromMetadata.length) return fromMetadata;

  const raw = String(request?.notes || '').trim();
  if (!raw) return [];
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  const tagged = lines
    .filter((line) => /client reject reason\s*:/i.test(line))
    .map((line) => line.replace(/client reject reason\s*:/i, '').trim())
    .filter(Boolean)
    .map((reason) => ({ reason }));
  return tagged;
};

export const extractRejectReason = (notes?: string | null): string => {
  const raw = String(notes || '').trim();
  if (!raw) return '';
  const lines = raw.split('\n').map((line) => line.trim()).filter(Boolean);
  const tagged = lines.filter((line) => /client reject reason\s*:/i.test(line));
  if (tagged.length) return tagged[tagged.length - 1].replace(/client reject reason\s*:/i, '').trim();
  const plain = lines.filter((line) => /reject|declin/i.test(line));
  if (plain.length) return plain[plain.length - 1];
  return '';
};

export const latestRejectReason = (request: Partial<SeatRequestItem> & { metadata?: any; notes?: string | null }): string => {
  const history = extractRejectHistory(request);
  if (history.length) return history[history.length - 1].reason;
  const fallback = extractRejectReason(request?.notes || '');
  return fallback || '';
};

export const rejectEventCount = (request: Partial<SeatRequestItem> & { metadata?: any; notes?: string | null }): number => {
  const history = extractRejectHistory(request);
  if (history.length) return history.length;
  const status = String((request as any)?.lifecycleStatus || (request as any)?.latestOfferStatus || request?.status || '').toUpperCase();
  if (['REJECTED', 'DECLINED', 'CLOSED_LOST'].includes(status)) return 1;
  return 0;
};

export const extractDecisionHistory = (request: Partial<SeatRequestItem> & { metadata?: any }): DecisionHistoryItem[] => {
  const metadata = ((request as any)?.metadata || {}) as any;
  const raw = Array.isArray(metadata.decisionHistory) ? metadata.decisionHistory : [];
  return raw
    .map((item: any) => ({
      at: String(item?.at || '').trim() || undefined,
      event: String(item?.event || '').trim().toUpperCase(),
      source: String(item?.source || '').trim() || undefined,
      reason: String(item?.reason || '').trim() || undefined,
      actorName: String(item?.actorName || '').trim() || undefined,
      offerId: String(item?.offerId || '').trim() || undefined,
      seats: Number.isFinite(Number(item?.seats)) ? Number(item.seats) : undefined,
      unitPriceUSD: Number.isFinite(Number(item?.unitPriceUSD)) ? Number(item.unitPriceUSD) : undefined,
      currencyCode: String(item?.currencyCode || '').trim().toUpperCase() || undefined,
    }))
    .filter((item: DecisionHistoryItem) => item.event)
    .sort((a: DecisionHistoryItem, b: DecisionHistoryItem) => {
      const aTs = a.at ? new Date(a.at).getTime() : 0;
      const bTs = b.at ? new Date(b.at).getTime() : 0;
      return aTs - bTs;
    });
};

export const isOfferAwaitingClient = (status?: string | null) => {
  const normalized = String(status || '').trim().toUpperCase();
  return normalized === 'AWAITING_CLIENT' || normalized === 'OFFER_SENT' || normalized === 'ALTERNATIVE_SENT';
};
